import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Copy, Loader2, Mail, RefreshCw, Trash2, UserPlus, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { TeamActivity } from "@/components/settings/TeamActivity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_LABELS = { OWNER: "Propriétaire", ADMIN: "Administrateur", MEMBER: "Membre" };
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
};

function Card({ title, icon: Icon, children, action }) {
  return (
    <section className="rounded-2xl border border-border bg-surface/60 p-5 space-y-4">
      <div className="flex items-center gap-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h2 className="text-sm font-semibold flex-1">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function RoleBadge({ role, tf }) {
  const cls = role === "OWNER" ? "bg-athlynx/10 text-athlynx border-athlynx/25"
    : role === "ADMIN" ? "bg-sky-500/10 text-sky-400 border-sky-500/25"
      : "bg-foreground/5 text-muted-foreground border-border";
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>
      {tf(ROLE_LABELS[role] || role)}
    </span>
  );
}

export const SectionTeam = () => {
  const { tf } = useLang();
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("MEMBER");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [name, setName] = useState("");

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/workspaces/current");
      setWs(r.data);
      setName(r.data.name || "");
    } catch (e) { console.error("workspace", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }
  if (!ws) return null;

  const isOwner = ws.role === "OWNER";
  const isManager = isOwner || ws.role === "ADMIN";
  const seats = ws.seats || {};

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(tf("Lien copié")); }
    catch { toast.error("Copie impossible"); }
  };

  const sendInvite = async () => {
    if (inviteBusy) return;
    setInviteBusy(true);
    try {
      const r = await api.post("/workspaces/invitations", { email: inviteEmail.trim(), role: inviteRole });
      setLastInviteUrl(r.data.invite_url);
      toast.success(r.data.email_sent ? tf("Email d'invitation envoyé.") : tf("Invitation créée — partagez le lien."));
      setInviteEmail("");
      reload();
    } catch (e) {
      toast.error(errText(e, tf("Invitation impossible")));
    } finally { setInviteBusy(false); }
  };

  const resend = async (inv) => {
    try {
      const r = await api.post(`/workspaces/invitations/${inv.invitation_id}/resend`);
      copy(r.data.invite_url);
      if (r.data.email_sent) toast.success(tf("Email d'invitation envoyé."));
    } catch (e) { toast.error(errText(e, "Erreur")); }
  };

  const revoke = async (inv) => {
    try { await api.delete(`/workspaces/invitations/${inv.invitation_id}`); toast.success(tf("Invitation révoquée")); reload(); }
    catch (e) { toast.error(errText(e, "Erreur")); }
  };

  const removeMember = async (m) => {
    try { await api.delete(`/workspaces/members/${m.user_id}`); toast.success(tf("Membre retiré")); reload(); }
    catch (e) { toast.error(errText(e, "Erreur")); }
  };

  const changeRole = async (m, role) => {
    try { await api.patch(`/workspaces/members/${m.user_id}`, { role }); reload(); }
    catch (e) { toast.error(errText(e, "Erreur")); }
  };

  const rename = async () => {
    try { await api.patch("/workspaces/current", { name }); toast.success(tf("Workspace renommé")); reload(); }
    catch (e) { toast.error(errText(e, "Erreur")); }
  };

  const leave = async () => {
    try {
      await api.post("/workspaces/leave");
      window.location.href = "/dashboard";
    } catch (e) { toast.error(errText(e, "Erreur")); }
  };

  return (
    <div className="space-y-5" data-testid="settings-team">
      <Card title={tf("Workspace")} icon={Building2}>
        {isOwner ? (
          <div className="flex gap-2 max-w-md">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              data-testid="workspace-name-input" />
            <Button variant="secondary" onClick={rename} disabled={!name.trim() || name === ws.name}
              data-testid="workspace-rename-btn">{tf("Renommer")}</Button>
          </div>
        ) : (
          <p className="text-sm">{ws.name}</p>
        )}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span data-testid="workspace-seats">
            {tf("Sièges utilisateurs")} : {seats.used}{seats.limit != null ? ` / ${seats.limit}` : ""}
            {seats.pending > 0 && ` (${seats.pending} ${tf("invitation(s) en attente")})`}
          </span>
        </div>
      </Card>

      <Card
        title={tf("Membres")} icon={Users}
        action={isManager && ws.can_invite && (
          <Button size="sm" onClick={() => { setLastInviteUrl(null); setInviteOpen(true); }} data-testid="invite-member-btn">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />{tf("Inviter un membre")}
          </Button>
        )}
      >
        {!ws.collaboration_available && (
          <div className="rounded-xl border border-athlynx/25 bg-athlynx/5 p-4 text-sm space-y-2" data-testid="team-upsell">
            <p>{tf("La collaboration multi-utilisateurs fait partie de l'offre Athlynx Agency.")}</p>
            {isOwner && (
              <Button asChild size="sm" variant="secondary">
                <Link to="/billing/plans">{tf("Passer à Agency")}</Link>
              </Button>
            )}
          </div>
        )}
        <ul className="divide-y divide-border/60" data-testid="members-list">
          {(ws.members || []).map((m) => (
            <li key={m.user_id} className="flex items-center gap-3 py-3">
              <div className="h-8 w-8 rounded-lg bg-secondary border border-border flex items-center justify-center text-xs font-bold shrink-0">
                {(m.name || m.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {m.name || m.email} {m.is_you && <span className="text-muted-foreground font-normal">({tf("vous")})</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
              </div>
              <RoleBadge role={m.role} tf={tf} />
              {isOwner && m.role !== "OWNER" && (
                <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                  <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`member-role-${m.user_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">{tf("Administrateur")}</SelectItem>
                    <SelectItem value="MEMBER">{tf("Membre")}</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {isManager && !m.is_you && m.role !== "OWNER" && !(ws.role === "ADMIN" && m.role === "ADMIN") && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-400 hover:text-rose-300"
                  onClick={() => removeMember(m)} aria-label={tf("Retirer du workspace")}
                  data-testid={`member-remove-${m.user_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
        {!isOwner && !ws.is_personal && (
          <Button variant="outline" size="sm" onClick={leave} data-testid="leave-workspace-btn">
            {tf("Quitter le workspace")}
          </Button>
        )}
      </Card>

      {isManager && (ws.invitations || []).length > 0 && (
        <Card title={tf("Invitations en attente")} icon={Mail}>
          <ul className="divide-y divide-border/60" data-testid="invitations-list">
            {ws.invitations.map((inv) => (
              <li key={inv.invitation_id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{inv.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {tf(ROLE_LABELS[inv.role] || inv.role)} · {tf("Expire le")} {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => resend(inv)}
                  aria-label={tf("Renvoyer et copier le lien")} data-testid={`invite-resend-${inv.invitation_id}`}>
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-400 hover:text-rose-300"
                  onClick={() => revoke(inv)} aria-label={tf("Révoquer")} data-testid={`invite-revoke-${inv.invitation_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {isManager && ws.collaboration_available && <TeamActivity />}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{tf("Inviter un membre")}</DialogTitle>
            <DialogDescription>
              {tf("Le collaborateur recevra un lien d'invitation valable 7 jours.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="email" placeholder={tf("Email du collaborateur")} value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MEMBER">{tf("Membre")}</SelectItem>
                <SelectItem value="ADMIN">{tf("Administrateur")}</SelectItem>
              </SelectContent>
            </Select>
            {lastInviteUrl && (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2" data-testid="invite-url-box">
                <p className="text-xs text-muted-foreground break-all">{lastInviteUrl}</p>
                <Button variant="secondary" size="sm" onClick={() => copy(lastInviteUrl)} data-testid="invite-copy-url">
                  <Copy className="h-3.5 w-3.5 mr-1.5" />{tf("Copier le lien")}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>{tf("Fermer")}</Button>
            <Button onClick={sendInvite} disabled={inviteBusy || !inviteEmail.includes("@")} data-testid="invite-send-btn">
              {inviteBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {tf("Envoyer l'invitation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
